import React, { useEffect, useState } from 'react';

import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { HorizontalSpace } from '@components/HorizontalSpace';
import { SelectButton } from '@components/SelectButton';
import { TradeConditionTest } from '@components/utilities/BookingInquiry/TradeConditionTest';
import { VerticalSpace } from '@components/VerticalSpace';
import {
  bookingInquiryStrings,
  bookingInquiryTradeConditionStrings,
  displayedTradeConditions,
} from '@data/bookingInquiry';
import {
  tradeConditionItems,
  TradeConditionItemTag,
  tradeConditionLabelToTags,
  tradeCondtionTagStrings,
} from '@data/invoice';
import { TradeCondition } from '@models/BookingInquiry';
import { makeMuiStyles, useCommonStyles, useMuiTheme } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { useField } from 'formik';

const strings = createStrings({
  ja: {
    selectPrompt: '貿易条件を選択してください',
    shipperCosts: 'SHIPPER（輸出者）が負担する費用：',
    consigneeCosts: 'CNEE（輸入者）が負担する費用:',
    checkFee: '検査料',
    none: 'なし',
    ok: '次へ',
    japanPaid: '日本払い',
    chinaPaid: '中国払い',
    goToTradeConditionTest: '貿易条件がわからない場合はこちら',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialog: {
    maxWidth: '960px !important',
  },
  tradeConditionItem: {
    ...common.flexInline,
    ...common.smallerText,
    lineHeight: 1.25,
    background: theme.palette.grey[50],
    height: 130,
    paddingRight: '1rem',
    [theme.breakpoints.down('sm')]: {
      paddingRight: '0.3rem',
      height: 240,
    },
  },
  tradeConditionContainer: {
    ...common.flexColumn,
    rowGap: 12,
  },
  tradeConditionItemHead: {
    width: 12,
    borderRight: `2px solid ${theme.palette.background.paper}`,
    '$selected &': {
      backgroundColor: theme.palette.primary.main,
    },
    [theme.breakpoints.down('sm')]: {
      width: 4,
    },
  },
  tradeConditionTitle: {
    ...common.flexColumnCenter,
    ...common.biggerText,
    ...common.textPrimary,
    ...common.fontWeight500,
    backgroundColor: theme.palette.background.paper,
    fontSize: 24,
    width: 80,
    height: 80,
    borderRadius: 40,
    border: `1px solid ${theme.palette.primary.main}`,
    '$selected &': {
      ...common.textWhite,
      backgroundColor: theme.palette.primary.main,
    },
    [theme.breakpoints.down('sm')]: {
      width: 40,
      height: 40,
      borderRadius: 20,
      ...common.bigText,
      ...common.fontWeight500,
    },
  },
  tradeConditionTitleWrapper: {
    ...common.flexColumnCenter,
    rowGap: '0.25rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '0.3rem',
      paddingRight: '0.3rem',
    },
  },
  costsContainer: {
    ...common.flexInline,
    gap: 16,
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      ...common.flexColumn,
      gap: 0,
    },
  },
  costsSubContainer: {
    flex: 1,
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    [theme.breakpoints.down('sm')]: {
      paddingTop: '0.2rem',
      paddingBottom: '0.2rem',
    },
  },
  contentRoot: {
    maxHeight: 450,
  },
  selected: {},
}));

interface TradeConditionItemProps {
  label: string;
  hasPickups: 'true' | 'false';
  shipperTags: TradeConditionItemTag[];
  receiverTags: TradeConditionItemTag[];
  selected: boolean;
  onClick: () => void;
}

const TradeConditionItem: React.FC<TradeConditionItemProps> = React.memo((props) => {
  const { label, hasPickups, shipperTags, receiverTags, selected, onClick } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const theme = useMuiTheme();

  const renderTitle = () => {
    switch (label) {
      case 'DDP':
      case 'EXW':
      case 'FCA':
        return label;
      case 'DDU 検査料-日本払い':
        return (
          <>
            <div style={{ marginBottom: -4 }}>DDU</div>
            <div className={classes.exSmallerText}>{strings.checkFee}</div>
            <div className={classes.smallerText}>{strings.japanPaid}</div>
          </>
        );
      case 'DDU 検査料-中国払い':
        return (
          <>
            <div style={{ marginBottom: -4 }}>DDU</div>
            <div className={classes.exSmallerText}>{strings.checkFee}</div>
            <div className={classes.smallerText}>{strings.chinaPaid}</div>
          </>
        );
    }
  };

  const renderItems = (tag: TradeConditionItemTag) => {
    const colors = {
      CHINA: theme.palette.accent.focus,
      D2D: theme.palette.primary.main,
      TAX: theme.palette.accent.main,
      CUSTOMS: theme.palette.grey[700],
    };

    return (
      <div style={{ color: colors[tag] }} key={tag}>
        {(tradeCondtionTagStrings as any)[tag] && <>{(tradeCondtionTagStrings as any)[tag]}（</>}
        {tradeConditionItems
          .filter((item) => item.tag === tag && (item.needsPickup ? hasPickups === 'true' : true))
          .map((item) => item.name)
          .join('、')}
        {(tradeCondtionTagStrings as any)[tag] && <>）</>}
      </div>
    );
  };

  return (
    <div
      role="TradeConditionItem"
      data-tag={label}
      className={clsx(classes.tradeConditionItem, selected && classes.selected)}
    >
      <div className={classes.tradeConditionItemHead} />
      <div className={classes.tradeConditionTitleWrapper}>
        <div className={classes.tradeConditionTitle}>{renderTitle()}</div>
        <div className={clsx(classes.smallerText, classes.textPrimary)}>
          {hasPickups === 'true'
            ? bookingInquiryStrings.needsPickup
            : bookingInquiryStrings.noPickup}
        </div>
      </div>

      <div className={classes.costsContainer}>
        <div className={classes.costsSubContainer}>
          <div
            className={clsx(
              classes.textGray900Important,
              classes.middleText,
              classes.fontWeight600Important,
            )}
          >
            {strings.shipperCosts}
          </div>
          <VerticalSpace height={4} />
          <div className={clsx(classes.textGray700)}>
            {shipperTags.map(renderItems)}
            {shipperTags.length === 0 && <div>{strings.none}</div>}
          </div>
        </div>

        <div className={classes.costsSubContainer}>
          <div
            className={clsx(
              classes.textGray900Important,
              classes.middleText,
              classes.fontWeight600Important,
            )}
          >
            {strings.consigneeCosts}
          </div>
          <VerticalSpace height={4} />
          <div className={clsx(classes.textGray700)}>
            {receiverTags.map(renderItems)}
            {receiverTags.length === 0 && <div>{strings.none}</div>}
          </div>
        </div>
      </div>

      <HorizontalSpace width={16} />

      <div className={classes.flexCenter}>
        <SelectButton type="button" selected={selected} onClick={onClick} />
      </div>
    </div>
  );
});

TradeConditionItem.displayName = 'TradeConditionItem'; // For testing

export interface TradeConditionDialogProps {
  name: string;
  hasPickupsFieldName: string;
  open: boolean;
  onClose: () => void;
}

export const TradeConditionDialog: React.FC<TradeConditionDialogProps> = (props) => {
  const { name, hasPickupsFieldName, open, onClose } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [showTradeConditionTest, setShowTradeConditionTest] = useState(false);

  const [field, , helpers] = useField<TradeCondition>(name);
  const [hasPickupsField] = useField<'true' | 'false'>(hasPickupsFieldName);

  const tradeConditionLabel = bookingInquiryTradeConditionStrings[field.value];

  useEffect(() => {
    setTimeout(() => {
      document
        .querySelector(
          `*[role="TradeConditionItem"][data-tag=${JSON.stringify(tradeConditionLabel)}]`,
        )
        ?.scrollIntoView({ block: 'nearest' });
    }, 200);
  }, [showTradeConditionTest, open, tradeConditionLabel]);

  const renderItems = () => (
    <>
      <ActionableDialogTitle>{strings.selectPrompt}</ActionableDialogTitle>
      <ActionableDialogContent classes={{ root: classes.contentRoot }}>
        <Button
          text={strings.goToTradeConditionTest}
          color="accent"
          shape="round"
          size="small"
          type="button"
          style={{ marginBottom: '1rem' }}
          onClick={() => setShowTradeConditionTest(true)}
        />

        <div className={classes.tradeConditionContainer}>
          {displayedTradeConditions.map((value) => (
            <TradeConditionItem
              key={value}
              label={bookingInquiryTradeConditionStrings[value]}
              hasPickups={hasPickupsField.value}
              shipperTags={tradeConditionLabelToTags[value].cn}
              receiverTags={tradeConditionLabelToTags[value].jp}
              selected={field.value === value}
              onClick={() => helpers.setValue(value)}
            />
          ))}
        </div>
      </ActionableDialogContent>
      <ActionableDialogContentDivider />
      <div style={{ padding: '1rem 24px' }}>
        <Button
          color="primary"
          text={strings.ok}
          size="small"
          data-testid="close-contact-filter"
          onClick={onClose}
          style={{ width: '100%' }}
          type="button"
        />
      </div>
    </>
  );

  return (
    <ActionableDialog
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.dialog,
      }}
    >
      {!showTradeConditionTest && renderItems()}
      {showTradeConditionTest && (
        <TradeConditionTest
          hasPickups={hasPickupsField.value}
          onGoBack={() => setShowTradeConditionTest(false)}
          onChange={(v) => {
            helpers.setTouched(true);
            helpers.setValue(v);
          }}
        />
      )}
    </ActionableDialog>
  );
};
